import { Calendar, CalendarPropsSingle } from "primereact/calendar";
import { useEffect, useRef, useState } from "react";
import { dateToMMDDYYYY, mmddyyyyToDate } from "../../date-utils";

type MmddyyyyCalendarProps = Omit<
  CalendarPropsSingle,
  "value" | "onChange" | "dateFormat" | "showButtonBar" | "onClearButtonClick"
> & {
  // Shows blank when not a valid mm/dd/yyyy date.
  value: string;
  // Receives "" when the user clears the field.
  onChange: (value: string) => void;
};

/**
 * A date picker over a "mm/dd/yyyy" string, with a clear button. Typed dates
 * reach `onChange` as soon as they parse, but the text is only reformatted on
 * blur.
 */
export const MmddyyyyCalendar: React.FC<MmddyyyyCalendarProps> = ({
  value,
  onChange,
  onBlur,
  ...calendarProps
}) => {
  // Calendar rewrites its input text, moving the cursor to the end, whenever
  // `value` changes by reference. So the Date is held in state rather than
  // rebuilt each render, and it is not updated from the user's own typing:
  // "03/1/2025" mid-edit would otherwise snap to "03/01/2025".
  const [date, setDate] = useState(() => mmddyyyyToDate(value));
  const typedValue = useRef<string | null>(null);

  useEffect(() => {
    if (value !== typedValue.current) {
      typedValue.current = null;
      setDate(mmddyyyyToDate(value));
    }
  }, [value]);

  return (
    <Calendar
      {...calendarProps}
      // Pinned rather than left to the locale default: the text must round-trip
      // through mmddyyyyToDate and dateToMMDDYYYY.
      dateFormat="mm/dd/yy"
      value={date}
      onChange={(e) => {
        if (!e.value) return;
        const next = dateToMMDDYYYY(e.value);
        typedValue.current = e.originalEvent?.type === "input" ? next : null;
        onChange(next);
      }}
      onBlur={(e) => {
        typedValue.current = null;
        setDate(mmddyyyyToDate(value));
        onBlur?.(e);
      }}
      showButtonBar
      onClearButtonClick={() => onChange("")}
    />
  );
};
